import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PocketBase, { BaseAuthStore } from 'pocketbase';
import { pb, pbPortal, pbEngineer, pocketBaseUrl } from '../lib/pb';
import { applyTenantBranding } from '../theme/tokens';
import { useToast } from './components/ToastProvider';

// /verify — Unified OTP magic-link exchange for ALL portals (admin, client, engineer)
//
// The OTP email from PocketBase always points to /#/portal/verify?otpId=…&code=…
// but we detect the user role after authentication and route to the appropriate portal.
//
// Session-collision fix (2026-08-25, incident "Client #2 link rendered Client #1"):
// the old flow short-circuited on ANY existing valid session BEFORE exchanging the
// OTP, so a second client's magic link was silently swallowed and routed to the
// first client's dashboard (the OTP was never consumed). It also exchanged client
// OTPs on the ADMIN store (`pb`) and copied the session across — which destroyed
// an admin session the moment a client link was clicked.
//
// New flow:
//   1. ALWAYS exchange the OTP — on a throwaway IN-MEMORY PocketBase client
//      (BaseAuthStore, no localStorage) so no existing session is touched by the
//      exchange itself (SDK 0.28 has no MemoryAuthStore; BaseAuthStore IS the
//      non-persistent base — LocalAuthStore adds the localStorage layer).
//   2. The exchanged record IS the identity — route by its role to the
//      role-appropriate persistent store: client → pbPortal, engineer → pbEngineer,
//      agency → pb. No cross-store copy, no clearing of other stores.
//   3. If that store already holds a session for a DIFFERENT account, the incoming
//      link wins (a link a person actually clicked must never be swallowed) and a
//      visible toast announces the replacement. Same-account re-links stay silent.
//   4. OTPs are single-use server-side: NEVER retry authWithOTP after any
//      post-exchange throw (a retry can only misreport "expired" — incident
//      2026-08-22). Show the resend screen instead.
export default function UnifiedVerify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { notify } = useToast();
  const ran = useRef(false);

  const otpId = params.get('otpId');
  const code = params.get('code');

  const [state, setState] = useState('checking'); // checking | ok | error
  const [errorMsg, setErrorMsg] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // The persistent store that owns each role's session slot (one per origin).
  const storeForRole = (role) => {
    if (role === 'client') return pbPortal;
    if (role === 'engineer') return pbEngineer;
    return pb; // agency_admin / agency_staff
  };

  const roleHome = (role) => {
    if (role === 'client' || role === 'engineer') return null; // resolved below
    return '/admin';
  };

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    if (!otpId || !code) {
      setErrorMsg('This sign-in link is incomplete or has expired.');
      setState('error');
      return;
    }

    // Scratch client: authenticates the OTP without persisting anything and
    // without touching pb/pbPortal/pbEngineer — so an admin session on this
    // browser survives a client magic-link click and vice versa.
    const scratch = new PocketBase(pocketBaseUrl, new BaseAuthStore());
    scratch.autoCancellation(false);

    (async () => {
      try {
        const auth = await scratch.collection('accounts').authWithOTP(otpId, code);
        const record = auth.record;
        const target = storeForRole(record.role);

        // Apply tenant branding for all users.
        if (record.tenant_id) {
          try {
            const tenants = await scratch.collection('tenants').getFullList();
            const tenant = tenants.find((t) => t.id === record.tenant_id) || null;
            applyTenantBranding({
              primary: tenant?.primary_color || '#173e36',
              primaryStrong: tenant?.primary_color || '#173e36',
              accent: tenant?.accent_color || '#dfff73',
            });
          } catch {
            // branding is cosmetic — ignore
          }
        }

        // The incoming link's account ALWAYS wins over a different account that
        // holds this store's slot. save() fires authStore.onChange → DataProvider
        // bumps → data reloads for the new session (setReady(false) synchronously).
        const existing = target.authStore.record;
        const swapped = Boolean(existing && existing.id !== record.id);
        target.authStore.save(auth.token, record);

        if (record.role === 'client' || record.role === 'engineer') {
          let slug = '';
          try {
            const tenants = await target.collection('tenants').getFullList();
            slug = tenants.find((t) => t.id === record.tenant_id)?.slug || '';
          } catch {
            slug = '';
          }
          if (swapped) {
            notify(`Signed in as ${record.name || 'this account'} — the previous session in this browser was replaced.`);
          }
          navigate(record.role === 'client' ? `/portal/${slug}` : '/engineer', { replace: true });
          setState('ok');
          return;
        }

        // agency_admin / agency_staff
        if (swapped) {
          notify(`Signed in as ${record.name || 'admin'} — the previous session in this browser was replaced.`);
        }
        navigate(roleHome(record.role), { replace: true });
        setState('ok');
      } catch (error) {
        // A genuine failure here means the OTP really is invalid (expired,
        // already used by a previous click/scanner, or replaced by a newer
        // "resend"). Do NOT retry — the exchange happened on the scratch client,
        // so nothing was partially written. Show the recovery screen.
        console.error('OTP verification failed:', error);
        setErrorMsg('This link has expired or already been used. Ask for a new one below.');
        setState('error');
      }
    })();
  }, [otpId, code, navigate, notify]);

  async function resend(event) {
    event.preventDefault();
    if (!email.trim() || sending) return;
    setSending(true);
    try {
      // One request is enough — both stores hit the same PocketBase backend
      // and a second requestOTP would mint a SECOND OTP + email (the newer
      // one replaces the first, so clicking the older email then reports
      // "expired"). Single call = single valid link in the inbox.
      await pb.collection('accounts').requestOTP(email.trim());
    } catch {
      // still show the "check your inbox" state — PB always answers 200-ish
    } finally {
      setSending(false);
      setSent(true);
    }
  }

  if (state === 'ok') {
    return <div className="standalone-page" />;
  }

  if (state === 'checking') {
    return <div className="standalone-page"><div className="panel login-card"><p className="eyebrow">Signing you in…</p></div></div>;
  }

  return (
    <div className="standalone-page login-page">
      <section className="panel settings-form login-card">
        <div className="brand admin-brand login-brand">
          <div className="brand-mark"><span>PP</span></div>
        </div>
        <h1>Sign-in link</h1>
        <p className="eyebrow">{errorMsg}</p>

        {!sent ? (
          <form onSubmit={resend}>
            <label>Your email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="username"
              />
            </label>
            <button className="primary-button" disabled={sending}>
              {sending ? 'Sending…' : 'Send me a new link'}
            </button>
          </form>
        ) : (
          <div className="send-ok">
            <p>If that email has a portal account, a fresh sign-in link is on its way — valid for 10 minutes.</p>
            <button className="primary-button" onClick={() => setSent(false)}>Send to a different email</button>
          </div>
        )}

        <p className="login-hint">
          Already have an account? <Link to="/admin">Admin sign in</Link> • <Link to="/portal">Client portal</Link>
        </p>
      </section>
    </div>
  );
}
